import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import {
  DropUp,
  Invoice,
  RevenueIcon,
  Sales,
  DropDwn,
  CartIcon,
  SortDownArrow,
} from "../../assets/svg";
import { useDispatch, useSelector } from "react-redux";
import { getDashboardData } from "../../redux/dashboard/slice";
import RippleLoader from "../../components/loader/RippleLoader";
import { useNavigate } from "react-router-dom";
import { Badge, SelectWithLabel } from "../../components/micro/input";
import Action from "../../components/common/Action";
import { updateProductInterest } from "../../redux/product/slice";
import ConfirmationModel from "../../components/common/ConfirmationModel";

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [popupData, setPopupData] = useState({ id: null, status: "" });
  const [confirmModal, setConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get the current year
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear.toString());
  // Generate an array from current year to last 5 years
  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    label: (currentYear - i).toString(),
    value: (currentYear - i).toString(),
  }));

  // Selector
  const { getDashboardDataList, loading } = useSelector((store) => ({
    getDashboardDataList:
      store?.dashboardData?.getDashboardDataStatus?.data?.data,
    loading: store?.dashboardData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getDashboardData({
        year: year,
      })
    );
  }, [year]);

  // update product status
  const updateStatus = () => {
    setIsLoading(true);

    dispatch(
      updateProductInterest({
        id: popupData?.id,
        data: { status: popupData?.status },
      })
    ).then((res) => {
      if (res.type === "updateProductInterest/fulfilled") {
        dispatch(
          getDashboardData({
            year: year,
          })
        );
      }
      setIsLoading(false);
      setConfirmModal(false);
    });
  };

  const handleYearChange = (e) => {
    setYear(e.target.value);
  };

  const data = {
    options: {
      chart: {
        type: "bar",
        toolbar: {
          show: true,
          tools: {
            download: false,
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          borderRadius: 2,
          endingShape: "rounded",
          distributed: false,
        },
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.25,
          gradientToColors: ["rgba(232, 91, 41, 0.4)"],
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 0.2,
          stops: [0, 100],
        },
      },
      colors: ["rgba(241, 91, 41, 1)"],
      tooltip: {
        theme: "dark",
        style: {
          fontSize: "14px",
          background: "#000",
          borderRadius: "8px",
        },
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        borderColor: "#E0E0E0",
        strokeDashArray: 4,
      },
      xaxis: {
        categories: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
        labels: {
          style: {
            colors: "#333",
            fontSize: "12px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#00000099",
            fontSize: "14px",
          },
          formatter: function (value) {
            return `$${value}`;
          },
        },
      },
    },
    series: [
      {
        name: "Revenue",
        data: [
          getDashboardDataList?.Revenues?.monthly_revenue?.January || 0,
          getDashboardDataList?.Revenues?.monthly_revenue?.February || 0,
          getDashboardDataList?.Revenues?.monthly_revenue?.March || 0,
          getDashboardDataList?.Revenues?.monthly_revenue?.April || 0,
          getDashboardDataList?.Revenues?.monthly_revenue?.May || 0,
          getDashboardDataList?.Revenues?.monthly_revenue?.June || 0,
          getDashboardDataList?.Revenues?.monthly_revenue?.July || 0,
          getDashboardDataList?.Revenues?.monthly_revenue?.August || 0,
          getDashboardDataList?.Revenues?.monthly_revenue?.September || 0,
          getDashboardDataList?.Revenues?.monthly_revenue?.October || 0,
          getDashboardDataList?.Revenues?.monthly_revenue?.November || 0,
          getDashboardDataList?.Revenues?.monthly_revenue?.December || 0,
        ],
      },
    ],
  };

  const Revenue = () => (
    <div className="bg-white rounded-lg shadow p-6 border border-borderLine relative">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <RevenueIcon className="w-8 h-8" />
          <h2 className="text-2xl font-normal font-Montserrat text-primaryBlacks">
            Revenue
          </h2>
        </div>
        <div className="w-24">
          <SelectWithLabel
            className="w-full"
            placeholder="Year"
            options={yearOptions}
            value={year}
            onChange={handleYearChange}
          />
        </div>
      </div>
      <div className="mt-4">
        <div className="flex items-center gap-2">
          <h3 className="text-[34px] text-primaryBlack font-normal font-Montserrat">
            ${getDashboardDataList?.Revenues?.totalRevenue?.toLocaleString()}
          </h3>
          <p className="text-textSecondary text-sm font-Proxima">
            Revenue base <br /> on year
          </p>
        </div>
        {/* Bar Chart */}
        <div>
          <Chart
            options={data.options}
            series={data.series}
            height="150%"
            type="bar"
          />
        </div>
      </div>
    </div>
  );

  const StatusCard = ({ title, count, percentage, danger, className = "" }) => (
    <div
      className={`p-4 w-full border border-borderLine ${
        danger ? "bg-red-100" : "bg-white"
      } ${className}`}
    >
      <h3 className="font-Montserrat text-xl font-medium text-primaryBlack">
        {title}
      </h3>
      <div className="flex items-center justify-between mt-2">
        <span className="text-sm font-normal font-Proxima text-primaryBlack">
          <p className="text-xs font-normal font-Proxima text-textSecondary">
            Total Count
          </p>
          {count?.toLocaleString()}
        </span>
        <span
          className={`text-xs text-white py-1 px-2 rounded-3xl flex items-center ${
            percentage?.includes("-") ? "bg-red" : "bg-bgGreen"
          }`}
        >
          {percentage?.includes("-") ? <DropDwn /> : <DropUp />}
          {percentage?.startsWith("-")
            ? percentage.replace("-", "")
            : percentage}
        </span>
      </div>
    </div>
  );

  const InvoiceCard = ({ label, value, danger, count, className }) => (
    <div className="flex flex-col">
      <span className="font-normal font-Proxima text-sm text-primaryBlack">
        {label}
      </span>
      <span className="font-Proxima text-sm text-textSecondary">
        Total Count: {count?.toLocaleString()}
      </span>
      <span className={`text-3xl font-normal font-Montserrat ${className}`}>
        $
        {parseFloat(value).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </span>
    </div>
  );

  return (
    <>
      <div className="p-8 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl font-medium font-Montserrat">
            Welcome Back! 👋
          </h1>
        </div>
        {loading ? (
          <RippleLoader />
        ) : (
          <>
            {/* Status Cards */}

            <div className=" mb-8">
              <div className="flex  justify-between  ">
                <div className="w-full rounded-tl-2xl rounded-bl-2xl">
                  <StatusCard
                    title="Active Company"
                    count={getDashboardDataList?.activeCompanies}
                    percentage={getDashboardDataList?.percentageCompanyIncrease}
                    className="rounded-tl-2xl rounded-bl-2xl"
                  />
                </div>
                <div className="w-full">
                  <StatusCard
                    title="Active Outlet"
                    count={getDashboardDataList?.activeOutlets}
                    percentage={getDashboardDataList?.percentageOutletIncrease}
                  />
                </div>
                <div className="w-full">
                  <StatusCard
                    title="On Recurring"
                    count={getDashboardDataList?.activeOnRecurring}
                    percentage={
                      getDashboardDataList?.percentageOnRecurringIncrease
                    }
                    className="rounded-tr-2xl rounded-br-2xl"
                  />
                </div>
              </div>
            </div>

            {/* Invoices, Revenue and Sales Ranking */}
            <div className="grid grid-cols-2 gap-6">
              {/* Invoices Section */}
              <div className="col-span-2 bg-white rounded-lg shadow p-6 border border-borderLine ">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Invoice className="w-8 h-8" />
                    <h2 className="text-2xl font-normal text-primaryBlack font-Montserrat">
                      Invoices
                    </h2>
                  </div>
                  <div
                    onClick={() =>
                      navigate("/sales", { state: { tab: "Invoice" } })
                    }
                  >
                    <p className="text-themeBtn border-b border-b-lightOrangeTheme cursor-pointer text-center text-base font-normal">
                      See All
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4 px-8">
                  <InvoiceCard
                    label="Quotation"
                    value={
                      getDashboardDataList?.invoices?.quotations?.totalAmount
                    }
                    count={
                      getDashboardDataList?.invoices?.quotations?.totalCount
                    }
                  />
                  <InvoiceCard
                    label="Awaiting Payment"
                    className={"text-lightorange"}
                    value={
                      getDashboardDataList?.invoices?.awaitingPayment
                        ?.totalAmount
                    }
                    count={
                      getDashboardDataList?.invoices?.awaitingPayment
                        ?.totalCount
                    }
                  />
                  <InvoiceCard
                    label="Paid"
                    className={"text-bgGreen"}
                    value={getDashboardDataList?.invoices?.paid?.totalAmount}
                    danger
                    count={getDashboardDataList?.invoices?.paid?.totalCount}
                  />
                </div>
              </div>

              {/* Revenue Section with Recharts */}
              <Revenue />

              {/* Salesperson Ranking */}
              <div className="bg-white rounded-lg shadow p-6 border border-borderLine h-3/4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Sales className="w-8 h-8" />
                    <h2 className="text-2xl font-normal font-Montserrat text-primaryBlack">
                      Sales person ranking
                    </h2>
                  </div>
                  <div onClick={() => navigate("/sales")}>
                    <p className="text-themeBtn border-b border-b-lightOrangeTheme cursor-pointer text-center text-base font-normal">
                      View All
                    </p>
                  </div>
                </div>
                <p className="font-Proxima text-sm text-textSecondary">
                  Current Year till date
                </p>
                <div className="mt-2 overflow-y-auto h-3/4">
                  <table className="w-full text-sm table-fixed rounded-t-lg">
                    <thead className="text-sm text-gray-700 bg-bgLightTheme rounded-t-lg border-b  border-b-lightPrimaryGrey font-semibold text-start">
                      <tr className="text-gray-500 bg-lightTheme">
                        <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-primaryBlack text-start">
                          Rank
                        </th>
                        <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-primaryBlack text-start">
                          Name
                        </th>
                        <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-primaryBlack text-start">
                          Sales Amount
                        </th>
                      </tr>
                    </thead>
                    {getDashboardDataList?.salesPersonRanking?.map(
                      (item, index) => {
                        return (
                          <tbody
                            className="text-sm font-normal text-primaryBlack"
                            key={index}
                          >
                            <tr>
                              <td className="px-5 py-1">{index + 1}</td>
                              <td className="px-5 py-1">{item?.name}</td>
                              <td className="px-5 py-1">
                                ${item?.totalAmount?.toLocaleString()}
                              </td>
                            </tr>
                          </tbody>
                        );
                      }
                    )}
                  </table>
                </div>
              </div>
            </div>
            {/* intrest */}
            <div className="bg-white rounded-lg shadow p-6 border border-borderLine mt-4">
              <div className="flex items-center justify-between">
                <div className="flex gap-4 items-center">
                  <CartIcon />
                  <p className="text-2xl">Product Interest</p>
                </div>
                <div onClick={() => navigate("/product-interest")}>
                  <p className="text-themeBtn border-b border-b-lightOrangeTheme cursor-pointer text-center text-base font-normal">
                    View All
                  </p>
                </div>
              </div>
              <div>
                <p className="text-textSecondary text-sm">
                  Product Interest Waiting for Follow Ups
                </p>
              </div>
              {/* table */}
              <div className={` rounded-lg bg-white  mt-4`}>
                <table className="w-full text-sm table-fixed">
                  {/* Table Header */}
                  <thead className="text-sm text-primaryBlack bg-bgLightTheme font-normal text-start">
                    <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                      No.
                    </th>
                    <th
                      scope="col"
                      className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
                    >
                      <div
                        className="flex items-center"
                        onClick={() => {
                          setSortBy("company");
                          setSortDirection(
                            sortDirection === "desc" ? "asc" : "desc"
                          );
                        }}
                      >
                        <span>Company</span>
                        <SortDownArrow
                          className={`ml-1 transition-transform duration-300 ${
                            sortDirection === "asc" && sortBy === "company"
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </div>
                    </th>
                    <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                      <div
                        className="flex items-center"
                        onClick={() => {
                          setSortBy("outlet");
                          setSortDirection(
                            sortDirection === "desc" ? "asc" : "desc"
                          );
                        }}
                      >
                        <span>Outlet</span>
                        <SortDownArrow
                          className={`ml-1 transition-transform duration-300 ${
                            sortDirection === "asc" && sortBy === "outlet"
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </div>
                    </th>
                    <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                      Sales Person
                    </th>
                    <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                      Item Quantity
                    </th>
                    <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                      <div
                        className="flex items-center"
                        onClick={() => {
                          setSortBy("date");
                          setSortDirection(
                            sortDirection === "desc" ? "asc" : "desc"
                          );
                        }}
                      >
                        <span>Date</span>
                        <SortDownArrow
                          className={`ml-1 transition-transform duration-300 ${
                            sortDirection === "asc" && sortBy === "date"
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </div>
                    </th>
                    <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                      <div
                        className="flex items-center"
                        onClick={() => {
                          setSortBy("status");
                          setSortDirection(
                            sortDirection === "desc" ? "asc" : "desc"
                          );
                        }}
                      >
                        <span>Status</span>
                        <SortDownArrow
                          className={`ml-1 transition-transform duration-300 ${
                            sortDirection === "asc" && sortBy === "status"
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </div>
                    </th>
                    <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                      <div
                        className="flex items-center"
                        onClick={() => {
                          setSortBy("amount");
                          setSortDirection(
                            sortDirection === "desc" ? "asc" : "desc"
                          );
                        }}
                      >
                        <span>Amount</span>
                        <SortDownArrow
                          className={`ml-1 transition-transform duration-300 ${
                            sortDirection === "asc" && sortBy === "amount"
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </div>
                    </th>
                    <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                      Actions
                    </th>
                  </thead>
                  {getDashboardDataList?.product_interset?.map(
                    (item, index) => {
                      return (
                        <tbody
                          className="text-sm font-normal text-primaryBlack"
                          key={index}
                        >
                          <tr
                            key={index}
                            className="font-Proxima shadow-sm text-mgrey hover:bg-lightprimaryorange"
                          >
                            <td className="px-4 py-3">{item?.id}</td>
                            <td className="px-4 py-3">{item?.company_name}</td>
                            <td className="px-4 py-3">{item?.outlet_name}</td>
                            <td className="px-4 py-3">
                              {item?.user_name ?? "-"}
                            </td>
                            <td className="px-4 py-3 text-themeBtn cursor-pointer underline">
                              {item?.total_request}
                            </td>
                            <td className="px-4 py-3">
                              {item?.date
                                ? new Date(item?.date).toLocaleDateString(
                                    "en-GB"
                                  )
                                : "-"}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex justify-start ">
                                <Badge
                                  className={`capitalize whitespace-nowrap ${
                                    item?.status === "in_talk"
                                      ? "border-lightBlue text-chipBlue"
                                      : item?.status === "pending"
                                      ? "border-stepGray text-primaryBlack"
                                      : ""
                                  }`}
                                  value={
                                    item?.status === "in_talk"
                                      ? "In Talk"
                                      : item?.status === "pending"
                                      ? "Pending"
                                      : item?.status == null
                                      ? "-"
                                      : "-"
                                  }
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {item?.amount != null ? `$${(+item.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "-"}
                            </td>
                            <td className="flex px-5 py-3">
                              <Action
                                optionList={
                                  <>
                                    {item?.status === "pending" && (
                                      <>
                                        <li
                                          className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                          onClick={() => {
                                            setConfirmModal(true);
                                            setPopupData({
                                              id: item?.id,
                                              status: "in_talk",
                                            });
                                          }}
                                        >
                                          In-Talk
                                        </li>
                                        <li
                                          className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                          onClick={() => {
                                            setConfirmModal(true);
                                            setPopupData({
                                              id: item?.id,
                                              status: "closed",
                                            });
                                          }}
                                        >
                                          Closed
                                        </li>
                                        <li
                                          className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                          onClick={() => {
                                            setConfirmModal(true);
                                            setPopupData({
                                              id: item?.id,
                                              status: "rejected",
                                            });
                                          }}
                                        >
                                          Rejected
                                        </li>
                                      </>
                                    )}

                                    {item?.status === "in_talk" && (
                                      <>
                                        <li
                                          className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                          onClick={() => {
                                            setConfirmModal(true);
                                            setPopupData({
                                              id: item?.id,
                                              status: "closed",
                                            });
                                          }}
                                        >
                                          Closed
                                        </li>
                                        <li
                                          className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                          onClick={() => {
                                            setConfirmModal(true);
                                            setPopupData({
                                              id: item?.id,
                                              status: "rejected",
                                            });
                                          }}
                                        >
                                          Rejected
                                        </li>
                                      </>
                                    )}
                                  </>
                                }
                              />
                            </td>
                          </tr>
                        </tbody>
                      );
                    }
                  )}
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {confirmModal && (
        <div>
          <ConfirmationModel
            confirmText={`Change Status to ${popupData.status}`}
            onConfirm={updateStatus}
            onClose={() => setConfirmModal(false)}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default Dashboard;
